import { z } from 'zod';
import { requireFields, yesNo } from '@utils/form-schema';
import type { IHistorySocialModel } from '@/types/history/history-social';

type TFlags = Pick<
    IHistorySocialModel,
    'askSexualSocialQuestions' | 'hasUsedTobacco' | 'isFemale' | 'isMale'
>;

const s = z.string();

const drugRow = z.object({
    recreationalDrug: s,
    yearsAgo: s,
    hasAbusedDrug: yesNo,
    hasReceivedTreatment: yesNo,
});

const baseSchema = z.object({
    tobaccoYears: s,
    tobaccoPacks: s,
    tobaccoFrequency: s,

    hasUsedAlcohol: yesNo,
    alcoholDrinks: s,
    alcoholFrequency: s,
    usesAlcohol: yesNo,
    hasAbusedAlcohol: yesNo,

    hasUsedDrugs: yesNo,
    drugUseHistory: z.array(drugRow),

    hasHadLegalIssues: yesNo,
    hasBeenSentencedToPrison: yesNo,
    prisonWhenAndWhere: s,
    hasTraveledLastYear: yesNo,
    travelLocations: s,

    hasHadHighRiskSex: yesNo,
    hasHadSexWithMmRelationsMale: yesNo,
    hasHadMmSex: yesNo,
    hasHadSexWithProstitute: yesNo,
    hasProstituted: yesNo,
    hasHadSexWithNeedleDrugUser: yesNo,
    hasUsedNeedleDrugs: yesNo,
    hasBeenInJailOver72: yesNo,
    hasHadStd: yesNo,
    hasBeenOnHemodialysis: yesNo,
    hasHadSuicidalThoughtsAttempts: yesNo,
});

type TValues = z.infer<typeof baseSchema>;

export const createHistorySocialSchema = (model: TFlags) =>
    baseSchema.superRefine((data: TValues, ctx) => {
        if (model.hasUsedTobacco === true) {
            requireFields(ctx, data, 'tobaccoYears', 'tobaccoPacks', 'tobaccoFrequency');
        }

        requireFields(ctx, data, 'hasUsedAlcohol');

        if (data.hasUsedAlcohol === '1') {
            requireFields(ctx, data, 'alcoholDrinks', 'alcoholFrequency', 'usesAlcohol', 'hasAbusedAlcohol');
        }

        requireFields(ctx, data, 'hasUsedDrugs');

        // A drug row is only validated once a drug has been picked for it.
        if (data.hasUsedDrugs === '1') {
            data.drugUseHistory.forEach((row, index) => {
                if (!row.recreationalDrug) return;

                const rowIssue = (field: keyof typeof row) =>
                    ctx.addIssue({
                        code: 'custom',
                        path: ['drugUseHistory', index, field],
                        message: 'Required',
                    });

                if (!row.yearsAgo) rowIssue('yearsAgo');
                if (!row.hasAbusedDrug) rowIssue('hasAbusedDrug');
                if (row.hasAbusedDrug === '1' && !row.hasReceivedTreatment) {
                    rowIssue('hasReceivedTreatment');
                }
            });
        }

        requireFields(ctx, data, 'hasHadLegalIssues');

        if (data.hasHadLegalIssues === '1') {
            requireFields(ctx, data, 'hasBeenSentencedToPrison');

            if (data.hasBeenSentencedToPrison === '1') {
                requireFields(ctx, data, 'prisonWhenAndWhere');
            }
        }

        requireFields(ctx, data, 'hasTraveledLastYear');

        if (data.hasTraveledLastYear === '1') {
            requireFields(ctx, data, 'travelLocations');
        }

        if (model.askSexualSocialQuestions) {
            requireFields(
                ctx,
                data,
                'hasHadHighRiskSex',
                'hasHadSexWithProstitute',
                'hasProstituted',
                'hasHadSexWithNeedleDrugUser',
                'hasUsedNeedleDrugs',
                'hasBeenInJailOver72',
                'hasHadStd',
                'hasBeenOnHemodialysis',
            );

            if (model.isFemale) {
                requireFields(ctx, data, 'hasHadSexWithMmRelationsMale');
            }

            if (model.isMale) {
                requireFields(ctx, data, 'hasHadMmSex');
            }
        }

        requireFields(ctx, data, 'hasHadSuicidalThoughtsAttempts');
    });
